import { LayoutDashboard, Search } from 'lucide-react';
import ActivityIcon from './ActivityIcon';
import { modulStranky } from '../../lib/moduly';

/**
 * ActivityBar — sloupec ikon v liště (mezi logem a spodním dockem).
 *
 * Co ukáže, rozhoduje počet modulů, ne role:
 *  - `rezimSekci === false` (do MAX_IKON_MODULU modulů): rovnou ikony modulů
 *  - `rezimSekci === true`: ikony sekcí; najetí / klik otevře panel sekce
 * V obou režimech je nahoře Přehled (má-li uživatel zaměstnance).
 *
 * @param {Object} props
 * @param {Object} props.user
 * @param {string} props.currentPage
 * @param {(pageId: string) => void} props.onNavigate
 * @param {Array} props.sekce - Sekce jen s moduly, které uživatel vidí
 * @param {boolean} props.rezimSekci
 * @param {string|null} [props.openSection]
 * @param {(sectionId: string|null, rect?: DOMRect) => void} [props.onSectionHover]
 * @param {(sectionId: string, rect: DOMRect) => void} [props.onSectionClick]
 * @param {() => void} [props.onMouseLeave] - Kurzor opustil lištu
 * @param {() => void} [props.onSearchClick]
 * @param {(itemId: string) => number} [props.getBadge]
 * @param {boolean} props.isDark
 */
export default function ActivityBar({
    user,
    currentPage,
    onNavigate,
    sekce,
    rezimSekci,
    openSection = null,
    onSectionHover,
    onSectionClick,
    onMouseLeave,
    onSearchClick,
    getBadge = () => 0,
    isDark = true,
}) {
    const hasEmployee = !!user?.zamestnanec_id;
    const aktualni = modulStranky(currentPage);
    // Najetí na ikonu, která není sekce, zavře otevřený panel.
    const zavriPanel = rezimSekci ? () => onSectionHover?.(null) : undefined;

    return (
        <div
            className="sidebar-activity-bar"
            onMouseLeave={onMouseLeave}
            style={{
                flex: 1, minHeight: 0,
                display: 'flex', flexDirection: 'column',
                padding: '4px 0',
                overflowY: 'auto', overflowX: 'hidden',
            }}
        >
            {hasEmployee && (
                <>
                    <ActivityIcon
                        Icon={LayoutDashboard}
                        label="Přehled"
                        active={currentPage === 'prehled'}
                        accentColor="#FF8A00"
                        isDark={isDark}
                        onClick={() => onNavigate('prehled')}
                        onMouseEnter={zavriPanel}
                    />
                    <Separator isDark={isDark} />
                </>
            )}

            {rezimSekci
                ? sekce.map((s) => (
                    <ActivityIcon
                        key={s.id}
                        Icon={s.Icon}
                        label={s.label}
                        isSection
                        active={aktualni?.sekce.id === s.id}
                        sectionOpen={openSection === s.id}
                        accentColor={s.color}
                        dot={s.items.some((i) => getBadge(i.id) > 0)}
                        isDark={isDark}
                        onClick={(e) => onSectionClick?.(s.id, e.currentTarget.getBoundingClientRect())}
                        onMouseEnter={(e) => onSectionHover?.(s.id, e.currentTarget.getBoundingClientRect())}
                    />
                ))
                : sekce.flatMap((s) => s.items.map((item) => (
                    <ActivityIcon
                        key={item.id}
                        Icon={item.Icon}
                        label={item.label}
                        active={aktualni?.item.id === item.id}
                        accentColor={s.color}
                        badge={getBadge(item.id)}
                        isDark={isDark}
                        onClick={() => onNavigate(item.id)}
                    />
                )))}

            <div style={{ flex: 1, minHeight: 8 }} />

            {onSearchClick && (
                <ActivityIcon
                    Icon={Search}
                    label="Hledat modul (Ctrl+K)"
                    isDark={isDark}
                    onClick={onSearchClick}
                    onMouseEnter={zavriPanel}
                />
            )}
        </div>
    );
}

function Separator({ isDark }) {
    return (
        <div
            aria-hidden="true"
            style={{
                height: 1, width: 28, margin: '6px auto',
                background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
            }}
        />
    );
}
